import React, { useState } from 'react';
import { usersApi } from '../api/users';
import { Role, ROLE_LABELS, ROLE_DESCRIPTIONS } from '../types/user';

interface InviteResult {
  email: string;
  delivered: 'email' | 'link';
  inviteUrl?: string;
}

interface Props {
  onClose: () => void;
  onInviteSent: (result: InviteResult) => void;
}

const InviteUserModal: React.FC<Props> = ({ onClose, onInviteSent }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('operator');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Введите email');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await usersApi.invite(email.trim(), role, 'email');
      onInviteSent({
        email: email.trim(),
        delivered: result.delivered,
        inviteUrl: result.inviteUrl,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Ошибка при создании приглашения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Пригласить пользователя</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Роль</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="admin">{ROLE_LABELS.admin}</option>
              <option value="treasurer">{ROLE_LABELS.treasurer}</option>
              <option value="operator">{ROLE_LABELS.operator}</option>
            </select>
            <div className="mt-2 p-2.5 bg-blue-50 border-l-4 border-blue-400 rounded-r-md">
              <p className="text-sm text-gray-700">{ROLE_DESCRIPTIONS[role]}</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-2 bg-red-100 text-red-800 rounded-md text-sm">{error}</div>
          )}

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
            >
              {loading ? 'Отправка...' : 'Отправить приглашение'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUserModal;
